import React, { useState, useEffect } from "react";
import { toasterState } from "Components/common/Toaster";
import Button from "Components/common/Button";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { settingsPopupState } from "./SettingsPopup";
import FormActionButtons from "./FormActionButtons";
import CircleLoader from "Components/common/CircleLoader";

const ChangeSubscriptionPopupContent = (props) => {
  const {
    email = "",
    activeLabelList = {},
    setActiveLabelList = () => {},
    planDetails = {},
  } = props || {};

  const [buttonLoader, setButtonLoader] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Same tier (Basic/Pro/...) exists per category (OUTREACH/DATA/BOTH), so the
  // plan must be identified by displayName + category, not displayName alone —
  // otherwise the dropdown shows duplicate-looking rows and the lookup in
  // handleUpdateSubscription resolves to the wrong plan id.
  const categoryLabel = (cat = "") => {
    const c = (cat || "").toLowerCase();
    if (c === "outreach") return "Outreach";
    if (c === "data") return "Data";
    if (c === "both") return "Both";
    return cat;
  };
  const planKey = (name = "", cat = "") =>
    `${(name || "").toLowerCase()}__${(cat || "").toLowerCase()}`;

  const planNameOptions = activePlans
    .filter((plan) => plan?.displayName)
    .map((plan) => ({
      label: `${plan.displayName} (${categoryLabel(plan.category)})`,
      value: planKey(plan.displayName, plan.category),
    }));

  const dataConfig = [
    {
      label: "Plan name",
      value: "changePlanName",
      type: "dropdown",
      options: planNameOptions,
    },
    {
      label: "Plan Type",
      value: "changePlanType",
      type: "dropdown",
      options: [
        { label: "Monthly", value: "monthly" },
        { label: "Annual", value: "annual" },
      ],
    },
  ];

  const updateSubscriptionValue = async (data = {}, email = "") => {
    if (email) {
      const config = {
        url: URLS.createPaidUser,
        method: "POST",
        data: { ...data, email: email },
      };
      let response = await makeApi(config);
      if (response?.data) {
        setButtonLoader(false);
        settingsPopupState.setShowPopup(false);
        toasterState.setToastMsg(
          "Subscriptions changed successfully",
          "success"
        );
      } else {
        Utils.showApiFailureResponse(response);
        settingsPopupState.setShowPopup(false);
      }
    } else {
      setButtonLoader(false);
    }
  };

  const handleUpdateSubscription = (e) => {
    e.preventDefault();
    setButtonLoader(true);
    const { changePlanType: planType = "", changePlanName: planName = "" } =
      activeLabelList || {};
    const currentSelectedPlan = activePlans.filter(
      (item) => planKey(item.displayName, item.category) === planName
    )[0];
    const config = {};
    if (planType === "annual") {
      config.planId = currentSelectedPlan?.annualPlanId;
      config.interval = 12;
      config.annual = true;
    } else {
      config.planId = currentSelectedPlan?.monthlyPlanId;
      config.interval = 1;
      config.annual = false;
    }
    updateSubscriptionValue(config, email);
  };

  const handleChange = (value = "", event) => {
    setActiveLabelList({
      ...activeLabelList,
      [value]: event?.currentTarget?.value,
    });
  };

  const getActiveSubscriptionPlans = async () => {
    setPlansLoading(true);
    const config = {
      url: URLS.getActiveSubscriptionPlan,
      method: "GET",
    };
    let response = await makeApi(config);
    if (response?.data) {
      setActivePlans(response?.data);
    }
    setPlansLoading(false);
  };

  useEffect(() => {
    getActiveSubscriptionPlans();
  }, []);

  // Preselect the customer's current plan + type once the active plans have
  // loaded (the options only exist after the fetch). Resolve the current plan by
  // displayName + category; if the category vocabulary doesn't line up, fall back
  // to the same tier by name, and finally to the first option. Done in one effect
  // so the fetch/preselect race can't leave it stuck on the first plan.
  useEffect(() => {
    if (!planNameOptions.length) return;
    const {
      planName = "",
      planType = "",
      planCategory = "",
    } = planDetails || {};

    const currentKey = planKey(planName, planCategory);
    let resolved = planNameOptions.find((opt) => opt.value === currentKey);
    if (!resolved) {
      const nameLc = (planName || "").toLowerCase();
      const byName = activePlans.find(
        (p) => (p.displayName || "").toLowerCase() === nameLc
      );
      if (byName) {
        resolved = { value: planKey(byName.displayName, byName.category) };
      }
    }
    const resolvedName = resolved ? resolved.value : planNameOptions[0].value;
    const resolvedType = (planType || "monthly").toLowerCase();

    setActiveLabelList((prev) => ({
      ...prev,
      changePlanName: resolvedName,
      changePlanType: resolvedType,
    }));
  }, [activePlans, planDetails]);

  if (plansLoading) {
    return (
      <div className="changeSubscriptionWrapper changeSubscriptionLoader">
        <CircleLoader />
      </div>
    );
  }

  return (
    <form
      className="changeSubscriptionWrapper"
      onSubmit={handleUpdateSubscription}
      onCancel={() => settingsPopupState?.setShowPopup(false)}
    >
      {dataConfig.map((item) => {
        const { label = "", value: itemValue = "", options = [] } = item || {};
        return (
          <div className="dataContainer" key={itemValue}>
            <div key={itemValue}>{label}</div>
            <select
              value={activeLabelList[itemValue]}
              onChange={(e) => handleChange(itemValue, e)}
            >
              {options?.map((option) => {
                const { label = "", value = "" } = option || {};
                return (
                  <option key={value} value={value}>
                    {label || value}
                  </option>
                );
              })}
            </select>
          </div>
        );
      })}
      <FormActionButtons
        submitBtnText="Update Plan"
        submitBtnClsName={`primaryButton updateSubscriptionButton ${
          buttonLoader ? "loading" : ""
        }`}
        cancelBtnClsName="secondaryButton cancelButton"
        cancel={() => settingsPopupState?.setShowPopup(false)}
        submitButtonLoader={buttonLoader}
      />
    </form>
  );
};

const ChangeSubscriptionPlan = (props) => {
  const { source = "changeSubscription" } = props || {};

  const handleChangeSubscription = () => {
    settingsPopupState.setShowPopup(true);
    settingsPopupState.setDefaultPopupvalue({
      cbk: () => settingsPopupState.setShowPopup(false),
      source,
    });
  };

  return (
    <Button
      id="changeSubscription"
      name="changeSubscription"
      type="button"
      className="primaryButton changeSubscriptionButton"
      btnText="Change Subscription"
      icon={false}
      loader={false}
      click={handleChangeSubscription}
    />
  );
};

export { ChangeSubscriptionPlan, ChangeSubscriptionPopupContent };
export default ChangeSubscriptionPlan;
